"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useQuery = void 0;
var _reactQuery = require("@tanstack/react-query");
var _useAuthorizationHeader = require("./useAuthorizationHeader");
var _utils = require("./utils");
var _utils2 = require("../utils");
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }
function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; } /*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
/**
 * Helper for query hooks, contains most of the logic in order to keep individual hooks small.
 * @param apiOptions - Options passed through to commerce-sdk-isomorphic
 * @param queryOptions - Options passed through to @tanstack/react-query
 * @param hookConfig - Config values that vary per API endpoint
 * @internal
 */
const useQuery = (apiOptions, queryOptions, hookConfig) => {
  const authenticatedMethod = (0, _useAuthorizationHeader.useAuthorizationHeader)(hookConfig.method);
  // This type assertion is NOT safe in all cases. However, we know that `requiredParameters` is
  // the list of parameters required by `Options`, and we know that in the default case (when
  // `queryOptions.enabled` is not set), we only execute the hook when `apiOptions` has all
  // required parameters. Therefore, we know that `apiOptions` satisfies `Options` in the default
  // case, so the type assertion is safe in the default case. We explicitly do NOT guarantee type
  // safety when `queryOptions.enabled` is set; when it is `true`, the callback may be called with
  // missing parameters. This will result in a runtime error. I think that this is an acceptable
  // trade-off, as the behavior is opt-in by the end user, and it feels like adding type safety
  // for this case would add significantly more complexity.
  const wrappedMethod = /*#__PURE__*/function () {
    var _ref = _asyncToGenerator(function* () {
      return yield authenticatedMethod(apiOptions);
    });
    return function wrappedMethod() {
      return _ref.apply(this, arguments);
    };
  }();
  return (0, _reactQuery.useQuery)(hookConfig.queryKey, wrappedMethod, _objectSpread(_objectSpread(_objectSpread({
    enabled:
    // Individual hooks can provide `enabled` checks that are done in ADDITION to
    // the required parameter check
    hookConfig.enabled !== false &&
    // The default `enabled` is "has all required parameters"
    (0, _utils.hasAllKeys)(apiOptions.parameters, hookConfig.requiredParameters)
  }, queryOptions), queryOptions !== null && queryOptions !== void 0 && queryOptions.retry ? {
    retry: (0, _utils2.onClient)() ? queryOptions === null || queryOptions === void 0 ? void 0 : queryOptions.retry : false
  } : {}), queryOptions !== null && queryOptions !== void 0 && queryOptions.retryOnMount ? {
    retryOnMount: (0, _utils2.onClient)() ? queryOptions === null || queryOptions === void 0 ? void 0 : queryOptions.retryOnMount : false
  } : {}));
};
exports.useQuery = useQuery;